/**
 * **자료 읽기.** 조회한 표가 무엇을 말하는지(한 행이 무엇인지, 상태 칸과 날짜 칸의 뜻, 어떤 이야기인지)를 정한다.
 *
 * 보고서 코드는 칸의 모양으로 뜻을 짐작했다(`reportData.ts`의 `statusColumn`, `dateColumn`). 중대결함 104건을 처리여부 「미처리」와
 * 계획일자로 읽어 「예정일 경과 미실시 101건」, 조치 「경과 101건 점검 완료」를 냈다(실제 테넌트, 2026-10-27). 이미 점검해서 찾은
 * 결함이라 할 일은 점검이 아니라 처리다.
 *
 * 분석 틀이 있는 화면은 `kb/experts.json` 틀의 `reading`을 쓴다(모델 판단 없이). 그 밖의 화면은 지금 짐작이 상태 칸을 찾은 표에만
 * 모델에게 묻는다. 이야기가 잘못 붙을 수 있는 표가 그것뿐이다. 칸과 값은 코드가 표와 맞대 검사한다. 읽기가 바꾸는 것은 「열린 문제」
 * (`issues`)뿐이다. 점검 미실시(`overdue`)와 목록(`list`)은 지금 짐작대로 둔다(설계 `docs/superpowers/specs/2026-10-27-data-reading-design.md`).
 */
import type { Expert } from './experts';
import type { TableData } from './files';
import type { Chat } from './plan';
import { statusColumn } from './reportData';
import type { AgentScreen } from './screenIndex';

export type ReadingStory = 'overdue' | 'issues' | 'list';
export type DateRole = 'due' | 'found' | 'done' | 'other';

/** 결과 기록에 남기는 읽기. 칸은 원자료 칸 id로 적고, 화면 이름은 `names`에 둔다(표마다 칸 이름이 달라도 다시 찾게). */
export type DataReading = {
  story: ReadingStory;
  /** 한 행이 무엇인지. */
  row?: string;
  /** 아직 끝나지 않았음을 가리는 칸과 그 값. 빈 값('')은 칸이 빈 행(완료일자가 빈 고장)이다. */
  status?: { column: string; open: string[] };
  date?: { column: string; role: DateRole };
  /** 급한 정도를 나누는 칸과 값의 차례(급한 것부터). */
  severity?: { column: string; order: string[] };
  /** 호기처럼 현장과 묶어 세는 칸. */
  unit?: string;
  /** 쏠림을 볼 칸, 중요한 차례로. */
  groups?: string[];
  /** 문제 내용이나 메모 글 칸. */
  notes?: string[];
  /** 칸 id → 화면 이름. */
  names?: Record<string, string>;
  /** 읽은 화면과 표. 조회가 여럿이면 모집단이 이 화면의 행만 읽기로 센다(칸 id `state`는 여러 화면에 있다, 독립 검토). */
  route?: string;
  rowsStateKey?: string;
  source: 'expert' | 'model';
};

/** 표의 칸 이름으로 풀어 둔 읽기. `TableData.reading`에 붙는다. */
export type TableReading = {
  story: ReadingStory;
  row?: string;
  status?: { column: string; open: string[] };
  date?: { column: string; role: DateRole };
  severity?: { column: string; order: string[] };
  unit?: string;
  groups: string[];
  notes: string[];
  source: 'expert' | 'model';
};

const STORIES = new Set<ReadingStory>(['overdue', 'issues', 'list']);
const ROLES = new Set<DateRole>(['due', 'found', 'done', 'other']);

function text(cell: unknown): string {
  return cell === null || cell === undefined ? '' : String(cell).trim();
}

/** 칸 이름을 견줄 꼴. 공백과 괄호 풀이(「결과(state)」, 「결과(%=전체」)를 뗀다. */
function squeeze(name: string): string {
  return name.replace(/\([^)]*\)?$/, '').replace(/\s+/g, '');
}

/**
 * 원자료 칸의 화면 이름. 칸 설명 끝의 「, 값: …」과 대시 뒤 설명, 코드 풀이 괄호를 뗀다. 모집단 표(`populationTable`)와 같은 이름이다.
 */
export function declaredName(column: { id: string; text?: string }): string {
  return (column.text ?? column.id).split(/,| — /)[0]?.replace(/\([^)]*=.*$/, '').trim() || column.id;
}

/** 표에서 그 칸의 자리. id, 화면 이름, 모집단이 바꿔 부른 이름(remark → 비고, sendflag → 전송)으로 찾는다. */
function columnIn(table: TableData, id: string, names: Record<string, string>): string | undefined {
  // 차례대로 찾는다: 제 이름과 id가 먼저, 바꿔 부른 이름은 나중. 표 차례로 찾으면 둘째 메모 칸이 첫째의 「비고」로 풀렸다(독립 재검토).
  const wanted = [names[id], id, id === 'sendflag' ? '전송' : '', /remark/i.test(id) ? '비고' : ''].filter(Boolean) as string[];
  for (const name of wanted) {
    const found = table.columns.find((column) => column === name) ?? table.columns.find((column) => squeeze(column) === squeeze(name));
    if (found) return found;
  }
  return undefined;
}

/**
 * 읽기를 표의 칸 이름으로 푼다. 없는 칸, 표에 없는 값은 버린다. 열린 문제인데 상태 칸도 긴급도 칸도 못 찾으면 읽기를 쓰지 않는다
 * (그 표에서는 열린 문제를 셀 수 없다).
 */
export function resolveReading(reading: DataReading | undefined, table: TableData | undefined): TableReading | undefined {
  if (!reading || !table?.columns.length) return undefined;
  const names = reading.names ?? {};
  const find = (id: string | undefined) => (id ? columnIn(table, id, names) : undefined);
  const valuesOf = (column: string) => {
    const at = table.columns.indexOf(column);
    return new Set(table.rows.map((row) => text(row[at])));
  };
  const statusColumn = find(reading.status?.column);
  const open = statusColumn ? (reading.status?.open ?? []).filter((value) => valuesOf(statusColumn).has(value.trim())) : [];
  const severityColumn = find(reading.severity?.column);
  const order = severityColumn ? (reading.severity?.order ?? []).filter((value) => valuesOf(severityColumn).has(value.trim())) : [];
  const dateColumn = find(reading.date?.column);
  const status = statusColumn && open.length ? { column: statusColumn, open } : undefined;
  const severity = severityColumn && order.length ? { column: severityColumn, order } : undefined;
  const taken = new Set([status?.column, severity?.column, dateColumn].filter(Boolean));
  const list = (ids: string[] | undefined) => Array.from(new Set((ids ?? []).map(find)
    .filter((one): one is string => !!one && !taken.has(one))));
  if (reading.story === 'issues' && !status && !severity) return undefined;
  return {
    story: reading.story,
    ...(reading.row ? { row: reading.row } : {}),
    ...(status ? { status } : {}),
    ...(dateColumn && reading.date ? { date: { column: dateColumn, role: reading.date.role } } : {}),
    ...(severity ? { severity } : {}),
    ...(find(reading.unit) ? { unit: find(reading.unit) } : {}),
    groups: list(reading.groups),
    notes: list(reading.notes),
    source: reading.source,
  };
}

/** 표에 읽기를 붙인다. 못 풀면 표 그대로. */
export function withReading(table: TableData | undefined, reading: DataReading | undefined): TableData | undefined {
  if (!table) return table;
  const resolved = resolveReading(reading, table);
  return resolved ? { ...table, reading: resolved } : table;
}

/** 열린 문제로 읽힌 표인가. 상태 칸 짐작과 경과 장치를 끄는 자리가 이것을 본다. */
export function isIssues(table: TableData | undefined): boolean {
  return table?.reading?.story === 'issues';
}

/** 분석 틀에 미리 적은 읽기(`experts.json`의 frames[].reading). 그 화면·표의 것이다. */
export function expertReading(
  route: string,
  rowsStateKey: string,
  experts: Expert[],
  columns: { id: string; text?: string }[],
): DataReading | undefined {
  for (const expert of experts) {
    const frame = (expert.frames ?? []).find((one) => one.route === route && one.rowsStateKey === rowsStateKey && one.reading);
    if (frame?.reading) {
      return {
        ...frame.reading, names: Object.fromEntries(columns.map((column) => [column.id, declaredName(column)])), route, rowsStateKey,
        source: 'expert',
      };
    }
  }
  return undefined;
}

/** 값이 적은 칸은 값과 건수, 날짜 칸은 범위, 나머지는 예 셋. 빈 칸 수도 적는다. */
export function describeColumns(table: TableData): string[] {
  return table.columns.map((name, at) => {
    const values = table.rows.map((row) => text(row[at]));
    const filled = values.filter(Boolean);
    const empty = values.length - filled.length;
    const counts = new Map<string, number>();
    filled.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
    const blank = empty ? `, 빈 칸 ${empty}` : '';
    if (!filled.length) return `- ${name}: 값 없음`;
    if (filled.every((value) => /^(?:19|20)\d{2}-?(?:0[1-9]|1[0-2])-?(?:0[1-9]|[12]\d|3[01])$/.test(value))) {
      const sorted = filled.map((value) => value.replace(/-/g, '')).sort();
      return `- ${name}: 날짜 ${sorted[0]}~${sorted[sorted.length - 1]}${blank}`;
    }
    if (counts.size <= 12) {
      const listed = Array.from(counts.entries()).sort((left, right) => right[1] - left[1]).slice(0, 8)
        .map(([value, count]) => `${value.slice(0, 30)} ${count}`);
      return `- ${name}: 값 ${listed.join(', ')}${counts.size > 8 ? ` 외 ${counts.size - 8}가지` : ''}${blank}`;
    }
    const examples = Array.from(counts.keys()).slice(0, 3).map((value) => value.slice(0, 30));
    return `- ${name}: 예 ${examples.join(', ')} (값 ${counts.size}가지)${blank}`;
  });
}

export const READ_PROMPT = `회사 ERP 화면에서 조회한 표 하나의 뜻을 정한다.

화면: {screen}
화면 설명: {description}
사용자 질문: {question}
행 수: {rows}
칸(이름: 값과 건수, 날짜 범위, 또는 예):
{columns}

정할 것:
- story: 이 표가 말하는 이야기 하나.
  overdue는 할 일을 할 날(예정일, 계획일, 기한)까지 못 했는지 보는 표다. 상태 칸에 아직 안 함을 뜻하는 값이 있고 그 일의 예정 날짜
  칸이 있다.
  issues는 행마다 발견되거나 생긴 문제(결함, 고장, 민원, 부적합)이고, 그 문제가 남아 있는지(처리 여부)나 얼마나 급한지(등급)를 보는
  표다. 행이 할 일의 계획이 아니라 처리할 문제다.
  list는 그 밖의 목록(명부, 장부, 잔액, 실적)이다.
- row: 한 행이 무엇인지 한 줄.
- status: 아직 끝나지 않았음(미처리, 미실시, 미완료)을 가리는 칸과 그 값들. 결재 상태처럼 문서 절차의 상태는 아니다. 끝낸 날짜 칸이
  비어 있는 것이 미처리라면 그 날짜 칸과 빈 값 "". 없으면 null.
- date: 이야기에 쓰는 날짜 칸 하나와 그 뜻. due는 할 날(예정, 계획, 기한), found는 문제가 발견되거나 생긴 날(점검일, 접수일, 발생일),
  done은 끝낸 날, other는 그 밖. issues는 found 날짜를 고른다. 없으면 null.
- severity: 급한 정도를 나누는 칸과 그 값의 차례(급한 것부터). 없으면 null.
- unit: 현장과 묶어 세는 설비 칸(호기 등). 없으면 null.
- groups: 쏠림을 볼 칸(항목, 현장, 담당 등) 셋까지, 중요한 차례로.
- notes: 문제 내용이나 메모를 적은 글 칸 셋까지.
칸 이름은 위 목록의 이름을, 값은 위에 나온 값을 그대로 쓴다.

JSON 하나만 낸다: {"story":"overdue|issues|list","row":"","status":{"column":"","open":[""]},"date":{"column":"","role":"due|found|done|other"},"severity":{"column":"","order":[""]},"unit":"","groups":[""],"notes":[""]}`;

/** 모델이 낸 읽기를 검사한다. 칸은 표에 있어야 하고 값은 그 칸에 있어야 한다. 칸 이름(표)을 id(원자료)로 바꿔 둔다. */
export function checkReading(raw: string, table: TableData, ids: Record<string, string> = {}): DataReading | undefined {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1] ?? raw;
  let value: Record<string, unknown>;
  try {
    value = JSON.parse(fenced.trim()) as Record<string, unknown>;
  } catch {
    return undefined;
  }
  const story = typeof value.story === 'string' ? value.story.trim().toLowerCase() as ReadingStory : undefined;
  if (!story || !STORIES.has(story)) return undefined;
  const has = (name: unknown): name is string => typeof name === 'string' && table.columns.indexOf(name.trim()) >= 0;
  const idOf = (name: string) => ids[name.trim()] ?? name.trim();
  const object = (one: unknown) => (one && typeof one === 'object' && !Array.isArray(one) ? one as Record<string, unknown> : undefined);
  const strings = (one: unknown) => (Array.isArray(one) ? one.filter((item): item is string => typeof item === 'string') : []);
  const status = object(value.status);
  const date = object(value.date);
  const severity = object(value.severity);
  const role = typeof date?.role === 'string' ? date.role.trim().toLowerCase() as DateRole : undefined;
  const names = Object.fromEntries(table.columns.map((name) => [idOf(name), name]));
  const reading: DataReading = {
    story,
    ...(typeof value.row === 'string' && value.row.trim() ? { row: value.row.trim() } : {}),
    ...(status && has(status.column) ? { status: { column: idOf(status.column), open: strings(status.open) } } : {}),
    ...(date && has(date.column) && role && ROLES.has(role) ? { date: { column: idOf(date.column), role } } : {}),
    ...(severity && has(severity.column) ? { severity: { column: idOf(severity.column), order: strings(severity.order) } } : {}),
    ...(has(value.unit) ? { unit: idOf(value.unit) } : {}),
    groups: strings(value.groups).filter(has).map(idOf),
    notes: strings(value.notes).filter(has).map(idOf),
    names,
    source: 'model',
  };
  // 값까지 맞대 본다. 열린 문제인데 상태도 긴급도도 못 찾으면 목록이다(셀 수 없는 열린 문제는 지금 짐작대로 둔다).
  const resolved = resolveReading(reading, table);
  if (story === 'issues' && !resolved) return { ...reading, story: 'list' };
  return reading;
}

/**
 * 조회한 것 가운데 모집단이 될 묶음(같은 화면·표, 행이 가장 많은 것)의 원자료 표. 칸은 화면 이름(`declaredName`)이다. 모집단 표
 * (`populationTable`)와 같은 칸 이름이라 읽기가 거기서도 풀린다.
 */
export function mainTable(
  retrievals: { label: string; route: string; rowsStateKey: string }[],
  rows: Record<string, unknown>,
  declared: Record<string, { id: string; text?: string }[]>,
): { route: string; rowsStateKey: string; columns: { id: string; text?: string }[]; table: TableData } | undefined {
  const listOf = (label: string) => (Array.isArray(rows[label]) ? rows[label] as Record<string, unknown>[] : []);
  const groups = new Map<string, typeof retrievals>();
  retrievals.forEach((one) => {
    if (!listOf(one.label).length || !declared[one.label]?.length) return;
    const key = `${one.route}\u0001${one.rowsStateKey}`;
    groups.set(key, [...(groups.get(key) ?? []), one]);
  });
  const [best] = Array.from(groups.values()).sort((left, right) => right.reduce((sum, one) => sum + listOf(one.label).length, 0)
    - left.reduce((sum, one) => sum + listOf(one.label).length, 0));
  const first = best?.[0];
  if (!best || !first) return undefined;
  const columns = declared[first.label] ?? [];
  return {
    route: first.route,
    rowsStateKey: first.rowsStateKey,
    columns,
    table: {
      columns: columns.map(declaredName),
      rows: best.flatMap((one) => listOf(one.label).map((item) => columns.map((column) => item[column.id]))),
    },
  };
}

/**
 * 조회 턴의 읽기: 분석 틀에 적어 둔 것만(표에서 풀려야 한다). 모델에게는 여기서 묻지 않는다. 상태 칸이 보이는 조회마다 묻자 호출이
 * 하나 늘고 답이 그것을 기다렸다(독립 검토). 모델 읽기는 문서를 만들 때 `readingForDocument`가 한다.
 */
export function readingFor(input: {
  retrievals: { label: string; route: string; rowsStateKey: string }[];
  rows: Record<string, unknown>;
  declared: Record<string, { id: string; text?: string }[]>;
  experts: Expert[];
}): DataReading | undefined {
  const main = mainTable(input.retrievals, input.rows, input.declared);
  if (!main) return undefined;
  const known = expertReading(main.route, main.rowsStateKey, input.experts, main.columns);
  return known && resolveReading(known, main.table) ? known : undefined;
}

/**
 * 문서를 만들 때의 읽기: 결과 기록에 읽기가 없고 지금 짐작이 상태 칸을 찾은 표만 모델에게 묻는다. 그 밖의 표는 이야기가 잘못 붙을
 * 일이 없다. 칸은 그 표의 이름 그대로 적는다(이름이 id다).
 */
export async function readingForDocument(input: {
  table: TableData | undefined; screen?: string; screens: AgentScreen[]; question: string; chat: Chat;
  /** 결과의 출처 글들(화면 이름이나 경로). 그중 분석 틀이 있는 화면이면 틀의 읽기만 쓰고 모델에게 묻지 않는다. */
  sources?: string[];
  experts?: Expert[];
}): Promise<DataReading | undefined> {
  if (!input.table?.rows.length || !statusColumn(input.table)) return undefined;
  // 출처 글은 화면 이름이거나 경로다.
  const routeOf = (source: string) => {
    const plain = source.replace(/\s*\(.*$/, '');
    return input.screens.find((one) => one.name === plain || one.route === plain)?.route ?? plain;
  };
  // 분석 틀이 있는 화면(점검결과)을 읽었으면 모델에게 묻지 않는다. 다듬은 점검 보고가 모델 판단 하나로 바뀌면 안 된다(독립 재검토).
  const framed = (input.experts ?? []).flatMap((expert) => expert.frames ?? [])
    .filter((frame) => frame.reading && (input.sources ?? []).map(routeOf).includes(frame.route));
  if (framed.length) {
    const table = input.table;
    return framed.map((frame): DataReading => ({ ...frame.reading!, route: frame.route, rowsStateKey: frame.rowsStateKey, source: 'expert' }))
      .find((one) => resolveReading(one, table));
  }
  const screen = input.screens.find((one) => one.name === input.screen || one.route === input.screen);
  return readData({
    question: input.question,
    screen: screen?.name ?? input.screen ?? '알 수 없음',
    description: screen?.description,
    table: input.table,
    chat: input.chat,
  });
}

/** 모델 읽기는 이만큼까지 기다린다. 넘으면 읽기 없이 지금 짐작대로 간다. */
const READ_TIMEOUT = 20000;

/** 모델에게 표의 뜻을 묻는다. 실패하거나 늦으면 없음. */
export async function readData(input: {
  question: string;
  screen: string;
  description?: string;
  table: TableData;
  ids?: Record<string, string>;
  chat: Chat;
}): Promise<DataReading | undefined> {
  const prompt = READ_PROMPT
    .replace('{screen}', () => input.screen)
    .replace('{description}', () => input.description ?? '없음')
    .replace('{question}', () => input.question.split('\n')[0] ?? input.question)
    .replace('{rows}', () => String(input.table.rows.length))
    .replace('{columns}', () => describeColumns(input.table).join('\n'));
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const raw = await Promise.race([
      // 게이트웨이는 단계 이름으로 모델을 고른다. 모르는 단계를 보내지 않고 검토 단계를 쓴다.
      input.chat(prompt, { step: 'review' }),
      new Promise<string>((_, reject) => { timer = setTimeout(() => reject(new Error('자료 읽기 시간 초과')), READ_TIMEOUT); }),
    ]);
    return checkReading(raw, input.table, input.ids);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.debug('[agent] 자료 읽기 못 함', String((error as Error)?.message ?? error).slice(0, 200));
    return undefined;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
